import { RefreshCcw } from 'lucide-react';
import React from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    children?: React.ReactNode;
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    icon?: React.ReactNode;
    iconOnly?: boolean;
    className?: string;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    iconOnly = false,
    className = '',
    disabled,
    type = 'button',
    ...rest
}) => {
    const isDisabled = disabled || loading;
    const classes = [
        'cu-button',
        `btn-${variant}`,
        `btn-${size}`,
        iconOnly && 'btn-icon-only',
        loading && 'btn-loading',
        className
    ].filter(Boolean).join(' ');

    return (
        <button
            type={type}
            className={classes}
            disabled={isDisabled}
            aria-busy={loading}
            {...rest}
        >
            {loading && <span className="btn-spinner" aria-hidden />}
            {!loading && icon && <span className="btn-icon">{icon}</span>}
            {!iconOnly && children != null && <span className="btn-text">{children}</span>}
        </button>
    );
};

export interface RefreshButtonProps extends Omit<ButtonProps, 'variant' | 'icon' | 'iconOnly'> {
    /** Показывать текст "Обновить" рядом с иконкой */
    showLabel?: boolean;
}

/**
 * Кнопка обновления с иконкой (и опционально текстом).
 */
export const RefreshBtn: React.FC<RefreshButtonProps> = ({
    showLabel = false,
    className = '',
    ...rest
}) => (
    <Button
        variant="primary"
        icon={<RefreshCcw size={16} />}
        iconOnly={!showLabel}
        className={`refresh-btn ${className}`.trim()}
        {...rest}
    >
        {showLabel ? 'Обновить' : undefined}
    </Button>
);

export interface ActionButtonProps extends Omit<ButtonProps, 'variant'> {
    variant?: ButtonVariant;
}

/**
 * Кнопка "Применить" (primary).
 */
export const ApplyBtn: React.FC<ActionButtonProps> = ({ children = 'Применить', className = '', ...rest }) => (
    <Button variant="primary" className={`apply-btn ${className}`.trim()} {...rest}>
        {children}
    </Button>
);

/**
 * Кнопка "Сбросить" (secondary).
 */
export const ClearBtn: React.FC<ActionButtonProps> = ({ children = 'Сбросить', className = '', ...rest }) => (
    <Button variant="secondary" className={`clear-btn ${className}`.trim()} {...rest}>
        {children}
    </Button>
);
